import PageHeader from "@/components/common/PageHeader";
import DefaultRoleTemplatesPage from "@/pages/governance/DefaultRoleTemplatesPage";
import RoleManagementPage from "@/pages/governance/RoleManagementPage";
import RolesPermissionsPage from "@/pages/governance/RolesPermissionsPage";
import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";

type TabKey = "platform-users" | "roles-permissions" | "role-templates";

const TABS: Array<{ key: TabKey; label: string; subtitle: string }> = [
  {
    key: "platform-users",
    label: "Platform Users",
    subtitle: "Manage admin portal accounts and roles.",
  },
  {
    key: "roles-permissions",
    label: "Tenant Roles & Permissions",
    subtitle: "Define tenant role templates and permission keys.",
  },
  {
    key: "role-templates",
    label: "Default Role Templates",
    subtitle:
      "Manage default permissions for new tenant roles — no code release needed.",
  },
];

function AccessControlPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  const activeTab = useMemo<TabKey>(() => {
    const tab = searchParams.get("tab");
    if (tab === "roles-permissions") return "roles-permissions";
    if (tab === "role-templates") return "role-templates";
    return "platform-users";
  }, [searchParams]);

  const tabMeta = TABS.find((t) => t.key === activeTab) ?? TABS[0];

  return (
    <div className="space-y-6">
      <PageHeader title="Access Control" subtitle={tabMeta.subtitle} />

      <div className="flex flex-wrap items-center gap-2">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setSearchParams({ tab: tab.key })}
            className={
              activeTab === tab.key
                ? "rounded-xl bg-slate-900 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-white"
                : "rounded-xl border border-slate-200 bg-white px-4 py-2 text-xs font-semibold uppercase tracking-wide text-slate-600 hover:bg-slate-50"
            }
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === "platform-users" ? (
        <RoleManagementPage embedded />
      ) : activeTab === "roles-permissions" ? (
        <RolesPermissionsPage embedded />
      ) : (
        <DefaultRoleTemplatesPage embedded />
      )}
    </div>
  );
}

export default AccessControlPage;
